import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  CreditCard,
  Wallet,
  FileText,
  Download,
  Filter,
  ChevronLeft,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { billingApi, getLocalizedValue } from '@/services/clinicApi';

const PAYMENT_STATUS_COLORS = {
  completed: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
  refunded: 'bg-purple-100 text-purple-700',
};

const ClinicBilling = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;

  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [activeTab, setActiveTab] = useState('overview');

  const [stats, setStats] = useState({
    totalRevenue: 0,
    pendingPayments: 0,
    completedPayments: 0,
    refunds: 0,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchBillingData();
  }, [isAuthenticated, navigate, selectedPeriod]);

  const fetchBillingData = async () => {
    setLoading(true);
    try {
      const [earningsRes, walletsRes, transactionsRes, methodsRes] = await Promise.all([
        billingApi.getEarnings().catch(() => null),
        billingApi.getWallets().catch(() => null),
        billingApi.getTransactions().catch(() => null),
        billingApi.getPaymentMethods().catch(() => null),
      ]);

      if (earningsRes?.data?.data) {
        setEarnings(earningsRes.data.data);
        // Calculate stats
        const total = earningsRes.data.data.reduce((sum, e) => sum + (e.amount || 0), 0);
        setStats(prev => ({ ...prev, totalRevenue: total }));
      }
      if (walletsRes?.data?.data) {
        setWallets(walletsRes.data.data);
      }
      if (transactionsRes?.data?.data) {
        setTransactions(transactionsRes.data.data);
      }
      if (methodsRes?.data?.data) {
        setPaymentMethods(methodsRes.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      toast.error(lang === 'ar' ? 'فشل في تحميل بيانات الفواتير' : 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-16 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="clinic-billing">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {lang === 'ar' ? 'الفواتير والمدفوعات' : 'Billing & Payments'}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">{lang === 'ar' ? 'هذا الأسبوع' : 'This Week'}</SelectItem>
                  <SelectItem value="month">{lang === 'ar' ? 'هذا الشهر' : 'This Month'}</SelectItem>
                  <SelectItem value="quarter">{lang === 'ar' ? 'هذا الربع' : 'This Quarter'}</SelectItem>
                  <SelectItem value="year">{lang === 'ar' ? 'هذا العام' : 'This Year'}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline">
                <Download className="h-4 w-4 mr-2" />
                {lang === 'ar' ? 'تصدير' : 'Export'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {lang === 'ar' ? 'إجمالي الإيرادات' : 'Total Revenue'}
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                    {stats.totalRevenue.toLocaleString()} {lang === 'ar' ? 'ل.س' : 'SYP'}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-green-100">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {lang === 'ar' ? 'مدفوعات معلقة' : 'Pending Payments'}
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                    {stats.pendingPayments}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-amber-100">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {lang === 'ar' ? 'مدفوعات مكتملة' : 'Completed'}
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                    {stats.completedPayments}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-teal-100">
                  <CheckCircle className="h-6 w-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {lang === 'ar' ? 'رصيد المحفظة' : 'Wallet Balance'}
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                    {wallets.reduce((sum, w) => sum + (w.balance || 0), 0).toLocaleString()} {lang === 'ar' ? 'ل.س' : 'SYP'}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-purple-100">
                  <Wallet className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">{lang === 'ar' ? 'نظرة عامة' : 'Overview'}</TabsTrigger>
            <TabsTrigger value="transactions">{lang === 'ar' ? 'المعاملات' : 'Transactions'}</TabsTrigger>
            <TabsTrigger value="earnings">{lang === 'ar' ? 'الأرباح' : 'Earnings'}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Transactions */}
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle>{lang === 'ar' ? 'آخر المعاملات' : 'Recent Transactions'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    {transactions.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                        <CreditCard className="h-12 w-12 mb-4 opacity-50" />
                        <p>{lang === 'ar' ? 'لا توجد معاملات' : 'No transactions'}</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {transactions.slice(0, 10).map((tx) => (
                          <div key={tx.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-full ${tx.type === 'credit' ? 'bg-green-100' : 'bg-red-100'}`}>
                                {tx.type === 'credit' ? (
                                  <ArrowDownRight className="h-4 w-4 text-green-600" />
                                ) : (
                                  <ArrowUpRight className="h-4 w-4 text-red-600" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-white">
                                  {tx.description || (lang === 'ar' ? 'معاملة' : 'Transaction')}
                                </p>
                                <p className="text-sm text-slate-500">
                                  {tx.created_at && format(new Date(tx.created_at), 'PPp', { locale })}
                                </p>
                              </div>
                            </div>
                            <span className={`font-semibold ${tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                              {tx.type === 'credit' ? '+' : '-'}{tx.amount?.toLocaleString()} {lang === 'ar' ? 'ل.س' : 'SYP'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Earnings Summary */}
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle>{lang === 'ar' ? 'ملخص الأرباح' : 'Earnings Summary'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    {earnings.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                        <DollarSign className="h-12 w-12 mb-4 opacity-50" />
                        <p>{lang === 'ar' ? 'لا توجد أرباح' : 'No earnings'}</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {earnings.slice(0, 10).map((earning) => (
                          <div key={earning.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white">
                                {earning.description || (lang === 'ar' ? 'ربح' : 'Earning')}
                              </p>
                              <p className="text-sm text-slate-500">
                                {earning.created_at && format(new Date(earning.created_at), 'PPp', { locale })}
                              </p>
                            </div>
                            <span className="font-semibold text-green-600">
                              +{earning.amount?.toLocaleString()} {lang === 'ar' ? 'ل.س' : 'SYP'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="transactions" className="mt-6">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle>{lang === 'ar' ? 'جميع المعاملات' : 'All Transactions'}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-500 text-center py-8">
                  {lang === 'ar' ? 'سيتم عرض جدول المعاملات هنا' : 'Transactions table will be displayed here'}
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="earnings" className="mt-6">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle>{lang === 'ar' ? 'تقرير الأرباح' : 'Earnings Report'}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-500 text-center py-8">
                  {lang === 'ar' ? 'سيتم عرض تقرير الأرباح هنا' : 'Earnings report will be displayed here'}
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ClinicBilling;
